/**
 * TimelineClip Component
 * 
 * Individual clip on the timeline with trim handles.
 * Simplified version without drag-drop.
 */

import React, { useState } from 'react';
import { EditorClip } from '../types';
import { formatTime } from '../utils/clipUtils';
import { getStatIcon, getStatColorClasses } from '../../../utils/statIconUtils';

interface TimelineClipProps {
  clip: EditorClip;
  width: number;
  isSelected: boolean;
  pixelsPerSecond: number;
  onSelect: () => void;
  onTrim: (startDelta: number, endDelta: number) => void;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
}

const TimelineClip: React.FC<TimelineClipProps> = ({
  clip,
  width,
  isSelected,
  pixelsPerSecond,
  onSelect,
  onTrim,
  onMoveUp,
  onMoveDown,
}) => {
  const [isTrimming, setIsTrimming] = useState<'start' | 'end' | null>(null);
  const [trimStartX, setTrimStartX] = useState(0);

  // Handle trim start
  const handleTrimMouseDown = (e: React.MouseEvent, edge: 'start' | 'end') => {
    e.stopPropagation();
    setIsTrimming(edge);
    setTrimStartX(e.clientX);
  };

  // Handle trim drag
  React.useEffect(() => {
    if (!isTrimming) return;

    const handleMouseMove = (e: MouseEvent) => {
      const deltaX = e.clientX - trimStartX;
      const deltaTime = deltaX / pixelsPerSecond;

      if (isTrimming === 'start') {
        onTrim(deltaTime, 0);
      } else {
        onTrim(0, deltaTime);
      }
      setTrimStartX(e.clientX);
    };

    const handleMouseUp = () => {
      setIsTrimming(null);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isTrimming, trimStartX, pixelsPerSecond, onTrim]);

  const duration = clip.endTime - clip.startTime;
  const colors = getStatColorClasses(clip.metadata?.statKey || null);

  return (
    <div
      style={{ width: Math.max(width, 40) }}
      className={`relative h-full flex-shrink-0 rounded overflow-hidden cursor-pointer ${
        isSelected ? 'ring-2 ring-blue-500' : ''
      }`}
      onClick={(e) => {
        e.stopPropagation();
        onSelect();
      }}
    >
      {/* Clip Background */}
      <div className={`absolute inset-0 ${isSelected ? 'bg-blue-500' : 'bg-gray-200'}`}>
        {clip.thumbnailUrl && (
          <div
            className="absolute inset-0 opacity-70"
            style={{
              backgroundImage: `url(${clip.thumbnailUrl})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/30" />
      </div>

      {/* Clip Content */}
      <div className="relative h-full flex flex-col justify-between p-1.5 pointer-events-none">
        {/* Top: Label + Minute */}
        <div className="flex items-center space-x-1 min-w-0">
          {clip.metadata?.statKey && (
            <div
              className={`w-4 h-4 rounded flex items-center justify-center text-xs flex-shrink-0 ${colors.bg} ${colors.text}`}
            >
              {getStatIcon(clip.metadata.statKey)}
            </div>
          )}
          <span className="text-xs text-white font-medium truncate">
            {clip.label || 'Clip'}
          </span>
          {clip.metadata?.matchMinute !== undefined && (
            <span className="text-xs text-white/60">{clip.metadata.matchMinute}'</span>
          )}
        </div>

        {/* Bottom: Team + Duration */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-1 min-w-0">
            {clip.metadata?.teamName && (
              <span className="text-xs text-white/60 truncate max-w-16">{clip.metadata.teamName}</span>
            )}
            <span className="text-xs text-white/70">• {formatTime(duration)}</span>
          </div>
          {isSelected && (
            <div className="flex items-center space-x-0.5 pointer-events-auto">
              {onMoveUp && (
                <button
                  onClick={(e) => { e.stopPropagation(); onMoveUp(); }}
                  className="w-4 h-4 bg-white/20 rounded hover:bg-white/40 flex items-center justify-center"
                  title="Move left"
                >
                  <span className="text-white text-xs">◀</span>
                </button>
              )}
              {onMoveDown && (
                <button
                  onClick={(e) => { e.stopPropagation(); onMoveDown(); }}
                  className="w-4 h-4 bg-white/20 rounded hover:bg-white/40 flex items-center justify-center"
                  title="Move right"
                >
                  <span className="text-white text-xs">▶</span>
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Trim Handles */}
      {isSelected && (
        <>
          <div
            className="absolute left-0 top-0 bottom-0 w-2 bg-yellow-500 cursor-ew-resize hover:bg-yellow-400 transition-colors z-10"
            onMouseDown={(e) => handleTrimMouseDown(e, 'start')}
          >
            <div className="absolute inset-y-0 left-0.5 w-0.5 bg-yellow-300" />
          </div>
          <div
            className="absolute right-0 top-0 bottom-0 w-2 bg-yellow-500 cursor-ew-resize hover:bg-yellow-400 transition-colors z-10"
            onMouseDown={(e) => handleTrimMouseDown(e, 'end')}
          >
            <div className="absolute inset-y-0 right-0.5 w-0.5 bg-yellow-300" />
          </div>
        </>
      )}

      {/* Clip borders */}
      <div className={`absolute inset-0 border rounded pointer-events-none ${isSelected ? 'border-blue-600' : 'border-gray-300'}`} />
    </div>
  );
};

export default TimelineClip;
